import { brand } from 'app/settings';
import {Component, computed, effect, inject, signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {EMPTY} from 'rxjs';
import {catchError, finalize, take} from 'rxjs/operators';
import {AuthenticationService} from '@modules/authentication';
import {
    IackathonLearnerSelfReviewsModalComponent
} from '../../components/iackathon-learner-self-reviews/iackathon-learner-self-reviews-modal/iackathon-learner-self-reviews-modal.component';
import {IackathonSave, IackathonSavesService} from '../../services/iackathon-saves.service';
import {
    IackathonPreviewZoomDialogComponent
} from "../../components/iackathon-preview-zoom-dialog/iackathon-preview-zoom-dialog.component";
import { IackathonMediaPlayerComponent } from '../../components/iackathon-media-player/iackathon-media-player.component';
import {
    filterLearnerDeliverableSaves,
    filterTrainerDeliverableSaves,
    getDeliverableModerationStatus,
    IackathonDeliverableModerationFilter
} from '../../models/iackathon-deliverable-moderation';
import {IackathonDeliverable, IackathonDeliverableModerationStatus, SPECIAL_MARK_OPTIONS} from '../../models/iackathon-save.state';

@Component({
    selector: 'app-iackathon-deliverable-list-page',
    imports: [
        MatFormFieldModule,
        MatIconModule,
        MatProgressSpinner,
        MatSelectModule,
        MatSnackBarModule,
        MatTooltipModule,
        TranslateModule,
        IackathonMediaPlayerComponent,
        RouterLink,
    ],
    templateUrl: './iackathon-deliverable-list-page.component.html',
    host: {
        '[class.iackathon-deliverable-list-page--has-card]': '!!selectedSave()',
    },
})
export class IackathonDeliverableListPageComponent {

    private router = inject(Router);
    private activatedRoute = inject(ActivatedRoute);
    private readonly dialog = inject(MatDialog);
    private iackathonSavesService = inject(IackathonSavesService);
    private authenticationService = inject(AuthenticationService);
    private snackBar = inject(MatSnackBar);
    private translate = inject(TranslateService);

    protected readonly isTrainerExperience = this.authenticationService.isTrainer();
    protected readonly isLearnerExperience = this.authenticationService.isLearner();
    protected readonly moderationFilters: IackathonDeliverableModerationFilter[] = [
        'all',
        'pending',
        'approved',
        'rejected',
    ];

    protected readonly specialMarkOptions = SPECIAL_MARK_OPTIONS;

    private readonly deliverableFormatIcons: { format: string; icon: string }[] = [
        {format: 'audio', icon: 'mic_rounded_fill'},
        {format: 'image', icon: 'wall_art_rounded_fill'},
        {format: 'pdf', icon: 'article_rounded_fill'},
        {format: 'slides', icon: 'view_carousel_rounded_fill'},
        {format: 'tech', icon: 'web'},
        {format: 'text', icon: 'article_rounded_fill'},
        {format: 'video', icon: 'slideshow_rounded_fill'},
    ];

    protected readonly brand = brand;
    protected readonly isLoading = signal(false);
    protected readonly hasLoaded = signal(false);
    protected readonly isModerationSaving = signal(false);
    protected readonly activeModerationFilter = signal<IackathonDeliverableModerationFilter>('all');
    private readonly urlParams = toSignal(this.activatedRoute.paramMap);
    private readonly queryParams = toSignal(this.activatedRoute.queryParamMap);
    protected readonly groupSaveId = computed(() => this.queryParams()?.get('id') ?? this.urlParams()?.get('id') ?? '');
    private readonly roleSaves = this.isTrainerExperience
        ? this.iackathonSavesService.trainerSaves
        : this.iackathonSavesService.learnerDeliverables;
    protected readonly groupSaves = computed(() => {
        if (this.isTrainerExperience) {
            return filterTrainerDeliverableSaves(this.roleSaves(), this.activeModerationFilter());
        }

        return filterLearnerDeliverableSaves(this.roleSaves());
    });
    protected readonly selectedSave = computed(() => this.groupSaves().find(save => save.id === this.groupSaveId()) ?? null);

    constructor() {
        if (this.roleSaves().length === 0) {
            this.isLoading.set(true);
            this.fetchSavesForRole().pipe(
                take(1),
                finalize(() => {
                    this.isLoading.set(false);
                    this.hasLoaded.set(true);
                })
            ).subscribe();
        } else {
            this.hasLoaded.set(true);
        }

        effect(() => {
            if (!this.isLearnerExperience || this.isLoading() || !this.hasLoaded()) {
                return;
            }

            if (this.groupSaveId() && !this.selectedSave()) {
                this.router.navigate(['/iackathon-deliverables']);
            }
        });
    }

    protected onSelectSaveChange(selectedSaveId: string): void {
        this.router.navigate(['/iackathon-deliverables'], {
            queryParams: {id: selectedSaveId},
        });
    }

    protected onModerationFilterChange(filter: IackathonDeliverableModerationFilter): void {
        this.activeModerationFilter.set(filter);
    }

    protected onApproveDeliverableButtonClick(save: IackathonSave): void {
        this.updateDeliverableModerationStatus(save, 'approved');
    }

    protected onRejectDeliverableButtonClick(save: IackathonSave): void {
        this.updateDeliverableModerationStatus(save, 'rejected');
    }

    protected onResetDeliverableModerationButtonClick(save: IackathonSave): void {
        this.updateDeliverableModerationStatus(save, 'pending');
    }

    /**
     * Returns the special mark most attributed to the given project by other groups (ties broken
     * by the highest mark value), or `null` when the project received no special mark.
     */
    protected getTopSpecialMark(save: IackathonSave): { value: number; pseudoIcon: string; label: string } | null {
        // Tally how many groups attributed each mark, reading their review of this project.
        const counts = new Map<number, number>();

        for (const reviewerSave of this.roleSaves()) {
            const mark = reviewerSave.attributes.content.state.groups_project_reviews?.[save.id]?.special_mark;
            if (mark === null || mark === undefined) {
                continue;
            }
            counts.set(mark, (counts.get(mark) ?? 0) + 1);
        }

        // Keep the most frequent mark, preferring the highest value on a tie.
        let topValue = -1;
        let topCount = 0;

        for (const [value, count] of counts) {
            if (count > topCount || (count === topCount && value > topValue)) {
                topValue = value;
                topCount = count;
            }
        }

        // Resolve the winning value to its display option (icon/label), or null if there was none.
        return this.specialMarkOptions.find(option => option.value === topValue) ?? null;
    }

    protected getDeliverableFormatIcon(format: string): string {
        return this.deliverableFormatIcons.find(entry => entry.format === format)?.icon ?? 'article_rounded_fill';
    }

    protected getBackHref(): string {
        return this.isTrainerExperience ? '/iackathon-intro' : '/iackathon-intro-learner';
    }

    protected getSaveIdeaLabel(save: IackathonSave): string {
        return save.attributes.content.state.solution_step?.chosen_idea?.idea ?? this.translate.instant('iackathon.no_idea');
    }

    protected getSaveMembers(save: IackathonSave) {
        const members = save.attributes.content.state.members;

        return Array.isArray(members) ? members : [];
    }

    protected getModerationStatus(save: IackathonSave): IackathonDeliverableModerationStatus {
        return getDeliverableModerationStatus(save.attributes);
    }

    protected getModerationFilterOptionKey(status: IackathonDeliverableModerationFilter): string {
        switch (status) {
            case 'approved':
                return 'iackathon.approved_deliverables';
            case 'pending':
                return 'iackathon.pending_deliverables';
            case 'rejected':
                return 'iackathon.rejected_deliverables';
            default:
                return 'iackathon.all_deliverable_status';
        }
    }

    protected getModerationStatusLabelKey(status: IackathonDeliverableModerationStatus): string {
        switch (status) {
            case 'approved':
                return 'iackathon.approved_deliverable';
            case 'rejected':
                return 'iackathon.rejected_deliverable';
            default:
                return 'iackathon.pending_deliverable';
        }
    }

    protected typeOfDeliverable(deliverable: IackathonDeliverable): 'audio' | 'image' | 'pdf' | 'unknown' | 'video' {
        if (['audio', 'image', 'pdf', 'video'].includes(deliverable.format)) {
            return deliverable.format as 'audio' | 'image' | 'pdf' | 'video';
        }

        const extensionSource = deliverable.filename?.includes('.') ? deliverable.filename : deliverable.url;
        const ext = extensionSource.split('?')[0].split('.').pop()?.toLowerCase();

        if (!ext) return 'unknown';
        switch (ext) {
            case 'jpg':
            case 'jpeg':
            case 'png':
            case 'gif':
            case 'bmp':
            case 'svg':
                return 'image';
            case 'mp3':
            case 'wav':
            case 'ogg':
                return 'audio';
            case 'mp4':
            case 'avi':
            case 'mov':
            case 'wmv':
                return 'video';
            case 'pdf':
                return 'pdf';
            default:
                return 'unknown';
        }
    }

    protected onLearnersSelfReviewsButtonClick(): void {
        const save = this.selectedSave();

        if (!save) {
            return;
        }

        this.dialog.open(IackathonLearnerSelfReviewsModalComponent, {
            data: {reviewsTarget: save.id}
        })
    }

    protected onZoomButtonClick() {
        const deliverable = this.selectedSave()?.attributes.content.state.deliverable;

        if (!deliverable) {
            return;
        }

        const format = this.typeOfDeliverable(deliverable)
        const path = deliverable.url

        this.dialog.open(IackathonPreviewZoomDialogComponent, {
            data: { format, path },
            panelClass: 'iackathon-preview-zoom-dialog-wrapper',
        });
    }

    private fetchSavesForRole() {
        return this.isTrainerExperience
            ? this.iackathonSavesService.fetchTrainerSaves()
            : this.iackathonSavesService.fetchLearnerAccessibleDeliverables();
    }

    private updateDeliverableModerationStatus(
        save: IackathonSave,
        status: IackathonDeliverableModerationStatus
    ): void {
        this.isModerationSaving.set(true);

        this.iackathonSavesService.updateDeliverableModerationStatus(save, status).pipe(
            take(1),
            catchError(() => {
                this.snackBar.open(this.translate.instant('iackathon.moderation_save_error'), undefined, {
                    duration: 5000,
                });

                return EMPTY;
            }),
            finalize(() => this.isModerationSaving.set(false)),
        ).subscribe();
    }
}
